import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { api, setAuthToken } from "../api/axios";

export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [token, setToken] = useState(localStorage.getItem("aw_token") || "");
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    localStorage.removeItem("aw_token");
    setAuthToken(null);
    setToken("");
    setUser(null);
  }, []);

  const verify = useCallback(async () => {
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      setAuthToken(token);
      const { data } = await api.get("/api/users/verify");
      setUser(data.user);
    } catch (e) {
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout, token]);

  useEffect(() => {
    verify();
  }, [verify]);

  const login = useCallback(async ({ email, password }) => {
    const { data } = await api.post("/api/users/login", { email, password });
    localStorage.setItem("aw_token", data.token);
    setAuthToken(data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const register = useCallback(async ({ email, password, name }) => {
    const { data } = await api.post("/api/users/register", { email, password, name });
    localStorage.setItem("aw_token", data.token);
    setAuthToken(data.token);
    setToken(data.token);
    setUser(data.user);
    return data;
  }, []);

  const loginWithToken = useCallback(
    async (newToken) => {
      if (!newToken) throw new Error("missing_token");
      localStorage.setItem("aw_token", newToken);
      setAuthToken(newToken);
      setToken(newToken);
      setLoading(true);
      try {
        const { data } = await api.get("/api/users/verify");
        setUser(data.user);
        return data;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      token,
      user,
      loading,
      login,
      loginWithToken,
      register,
      logout,
      verify,
      isAdmin: !!user?.is_admin,
    }),
    [loading, login, loginWithToken, logout, register, token, user, verify]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}


